import { proxy, ref } from 'valtio';
import { derive, proxyMap } from 'valtio/utils';

import { AtlassianClient } from '@canvas/atlassian-client';
import { FailureReasons, hostNotifier } from '@canvas/host-notifier';
import { safeGet, safeSet } from '@canvas/idb-cache';

import {
  DEVELOPMENT_ORIGIN,
  DEVELOPMENT_TENANTLESS_HOSTNAMES,
} from './site-service-constants';
import {
  getAvailableSitesUsedByCanvas,
  type ProductName,
  type Site,
  type AccessibleProductsRequest,
  PermissionId,
  type AccessibleProductsResponse,
  type Workspace,
  type ProductId,
} from './site-types';
import { toProductName } from './transformers/site-transformers';

const ACCESSIBLE_PRODUCTS_CACHE_KEY = 'canvas-accessible-products';
const ONE_HOUR_MS = 60 * 60 * 1000;

type CachedValue<ResponseType> = {
  response: ResponseType;
  time: number;
};

type WorkspaceWithProductId = Workspace & { productId: ProductId };

async function getCache<ResponseType>(key: string) {
  const cached = await safeGet<CachedValue<ResponseType>>(key);
  // ignore cached result if it is over an hour old
  if (!cached || cached?.time < Date.now() - ONE_HOUR_MS) {
    return undefined;
  }
  return cached.response;
}

const getAccessibleProductsCached = async () =>
  await getCache<AccessibleProductsResponse>(ACCESSIBLE_PRODUCTS_CACHE_KEY);

async function setAccessibleProductsCached(response: AccessibleProductsResponse) {
  await safeSet<CachedValue<AccessibleProductsResponse>>(ACCESSIBLE_PRODUCTS_CACHE_KEY, {
    response,
    time: Date.now(),
  });
}

export class SiteService {
  public initialized = false;
  public state: { jiraSites: Site[]; site: Site };

  private readonly observableSites = proxyMap<string, Site[]>();
  private readonly sites = new Map<string, Site[]>();

  constructor(
    private readonly baseUrl: URL = new URL('/gateway/api', globalThis.location.origin),
    private readonly client = new AtlassianClient(window.fetch.bind(window), baseUrl)
  ) {
    const self = this;

    const sites = derive({
      jiraSites: (get) =>
        [...get(self.observableSites).values()]
          .flatMap((sites) => sites.filter((site) => site.products.includes('jira')))
          .sort((a, z) => a.name.localeCompare(z.name)),
    });

    this.state = derive(
      {
        jiraSites: (get) => get(sites).jiraSites,
        site: (get) => {
          const jiraSites = get(sites).jiraSites;

          const firstJiraSite = jiraSites[0];
          const currentJiraSite = self.getJiraSiteForOrigin(this.baseUrl.origin);

          const currentSite = self.getSiteByOriginWithSites(
            this.baseUrl.origin,
            self.getAllSites()
          );

          const site = currentJiraSite ?? firstJiraSite ?? currentSite;
          return site
            ? proxy({
                name: site.name,
                cloudId: site.cloudId,
                products: site.products,
                url: site.url ? ref(site.url) : site.url,
                iconUrl: site.iconUrl ? ref(site.iconUrl) : site.iconUrl,
              })
            : site;
        },
      },
      { sync: true }
    );
  }

  public async loadSites() {
    if (this.hasSites()) {
      return;
    }

    await this.loadSitesFromAccessibleProducts();
  }
  private async loadSitesFromAccessibleProducts() {
    const fullUrl = this.baseUrl.href + '/accessible-products';

    try {
      const cache = await getAccessibleProductsCached();

      hostNotifier.reportExternalCheckpoint({
        source: FailureReasons.PRODUCT_API_FAILURE,
        location: 'site-service',
        endpoint: fullUrl,
        dependencyId: 'accessible-products',
      });

      const response =
        cache ??
        (await this.client.post<AccessibleProductsRequest, AccessibleProductsResponse>(
          '/accessible-products',
          {
            productIds: getAvailableSitesUsedByCanvas(),
            permissionIds: [PermissionId.WRITE],
          }
        ));

      if (!response.products) {
        hostNotifier.reportExternalFailure({
          source: FailureReasons.PRODUCT_API_FAILURE,
          location: 'site-service',
          endpoint: fullUrl,
          dependencyId: 'accessible-products',
        });
        return;
      }

      if (!cache) {
        await setAccessibleProductsCached(response);
      }

      this.sites.clear();
      const cloudIdToWorkspace = new Map<string, WorkspaceWithProductId[]>();
      let isCustomDomain = false;
      let orgId: string | null = null;

      // Transforms the result from accessible-products from a product to workspace map, to a cloudId to workspace map
      // Flatten the response to only the augmented the workspaces
      const workspaces = response.products.flatMap((product) => {
        return product.workspaces
          ? product.workspaces.map((workspace) => {
              return { ...workspace, productId: product.productId };
            })
          : [];
      });
      for (const workspace of workspaces) {
        const workspaceCloudId = workspace.cloudId;
        const workspaceUrlOrigin = new URL(workspace.workspaceUrl).origin;
        if (workspaceUrlOrigin === this.baseUrl.origin) {
          orgId = workspace.orgId;
          if (workspace.cloudUrl !== workspace.workspaceUrl) {
            isCustomDomain = true;
          }
        }
        const workspacesForCloudId = cloudIdToWorkspace.get(workspaceCloudId) ?? [];
        cloudIdToWorkspace.set(workspaceCloudId, workspacesForCloudId);
        workspacesForCloudId.push(workspace);
      }

      for (const cloudIdWorkspaces of cloudIdToWorkspace.values()) {
        // Splits the workspaces for a single cloudId by workspaceUrls. Different workspaces (e.g. different products)
        // can have the default workspace URL (the Atlassian site name) or a custom domain.
        const workspaceUrlToWorkspace = new Map<string, WorkspaceWithProductId[]>();
        for (const workspace of cloudIdWorkspaces) {
          const workspaceUrlOrigin = new URL(workspace.workspaceUrl).origin;
          const workspaceArray = workspaceUrlToWorkspace.get(workspaceUrlOrigin) ?? [];
          workspaceUrlToWorkspace.set(workspaceUrlOrigin, workspaceArray);
          workspaceArray.push(workspace);
        }
        for (const urlWorkspaces of workspaceUrlToWorkspace.values()) {
          // Removes any workspaces if we're on a custom domain, and the workspace orgId doesn't match the orgId of the
          // current site. If we're not a custom domain, everything is allowed.
          const filteredWorkspaces = urlWorkspaces.filter(
            (workspace) => !isCustomDomain || workspace.orgId === orgId
          );
          if (filteredWorkspaces.length === 0) {
            continue;
          }
          // If a workspaces have the same cloudId and the same workspaceURL, the display name and avatar will be the
          // same
          const firstWorkspace = filteredWorkspaces[0];
          const products = new Set(
            filteredWorkspaces
              .map((workspace) => toProductName(workspace.productId))
              .filter((productName): productName is ProductName => !!productName)
          );
          const site: Site = {
            name: firstWorkspace.workspaceDisplayName,
            url: ref(new URL(firstWorkspace.workspaceUrl)),
            products: Array.from(products).sort(),
            cloudId: firstWorkspace.cloudId,
            iconUrl: firstWorkspace.workspaceAvatarUrl
              ? ref(new URL(firstWorkspace.workspaceAvatarUrl))
              : null,
          };
          this.setSite(site);
        }
      }
      this.initialized = true;
    } catch (e) {
      hostNotifier.reportExternalFailure({
        source: FailureReasons.PRODUCT_API_FAILURE,
        location: 'site-service',
        endpoint: fullUrl,
        dependencyId: 'accessible-products',
      });
      throw e;
    }
    return null;
  }

  private setSite(site: Site) {
    const cloudId = site.cloudId;
    const siteArray = this.sites.get(cloudId) ?? [];
    siteArray.push(site);
    this.observableSites.set(site.cloudId, siteArray);
    this.sites.set(site.cloudId, siteArray);
  }

  public hasSites() {
    return this.sites.size > 0;
  }

  public getSites(productName: ProductName) {
    return Array.from(this.sites.values()).flatMap((sites) =>
      sites
        .filter((site) => new Set(site.products).has(productName))
        .sort((a, z) => a.name.localeCompare(z.name))
    );
  }

  public getAllSites() {
    return Array.from(this.sites.values())
      .flat()
      .sort((a, z) => a.name.localeCompare(z.name));
  }

  public getSitesByCloudId(cloudId: string) {
    return this.sites.get(cloudId) ?? [];
  }

  public getSiteByCloudIdAndProduct(cloudId: string, productName: ProductName) {
    return this.getSitesByCloudId(cloudId).find((s) => s.products.includes(productName));
  }

  public getConfluenceSiteForOrigin(origin: string): Site | undefined {
    return this.getSiteByOriginAndProduct(origin, 'confluence');
  }

  public getJiraSiteForOrigin(origin: string): Site | undefined {
    return this.getSiteByOriginAndProduct(origin, 'jira');
  }

  private getSiteByOriginAndProduct(
    origin: string,
    productName: ProductName
  ): Site | undefined {
    const currentSite = this.getSiteByOriginWithSites(origin, this.getAllSites());
    if (!currentSite) {
      return undefined;
    }
    return this.getSiteByCloudIdAndProduct(currentSite.cloudId, productName);
  }

  private getSiteByOriginWithSites(origin: string, sites: Site[]): Site | undefined {
    let hostname = new URL(origin).hostname;

    const isTenantlessDevelopment = DEVELOPMENT_TENANTLESS_HOSTNAMES.includes(hostname);
    if (isTenantlessDevelopment) {
      hostname = DEVELOPMENT_ORIGIN.hostname;
    }
    return sites.find((s) => s.url.hostname === hostname);
  }

  /**
   * Site service is used as a singleton
   */
  private static instance: SiteService | null = null;

  static getInstance = (): SiteService => {
    if (!SiteService.instance) {
      SiteService.instance = new SiteService();
    }

    return SiteService.instance;
  };
}

export { type Site } from './site-types';
